/* eslint-disable no-param-reassign */
import { db, functions, auth, remoteConfig } from '@/shared/services/firebase'
import { calculateShift, getThreeWeekPeriod, calculateOverHoursInPeriod, getHoliday, getEve } from '@/shared/services/invoice-calc'
import axios from 'axios'
import html2pdf from 'html2pdf.js'
import { fetchContract } from './contractService'
import checkTempContractShift from './shiftService'
import store from '../store'

/*
Why is there getCost and getSalary which do the exact same thing??
*/

const getCost = (currentUser, specialSalary, salary) => {
  if (specialSalary) {
    return specialSalary
  }
  const keys = ['default'].concat(Object.keys(salary).filter(key => currentUser.role.canWorkAs.includes(key)))
  if (currentUser.love) {
    keys.push('love')
  }
  let result = 0
  keys.forEach(key => {
    result = salary[key] > result ? salary[key] : result
  })
  return result
}

const getSalary = (currentUser, salary, specialSalary = false) => {
  if (specialSalary) {
    return specialSalary
  }
  const keys = ['default'].concat(Object.keys(salary).filter(key => currentUser.role.canWorkAs?.includes(key)))
  if (currentUser.love) {
    keys.push('love')
  }
  let result = 0
  keys.forEach(key => {
    result = salary[key] > result ? salary[key] : result
  })
  return result
}

const savePayrolls = async payrollList => {
  const batch = db.batch()
  payrollList.forEach(payroll => {
    const newPayroll = db.collection('payrolls').doc()
    batch.set(newPayroll, {
      ...payroll,
    })
  })
  await batch.commit()
}

const fetchShifts = ({ from, to, isQuickShift, getAll = false, assignedTo = false }) => new Promise((resolve, reject) => {
  let query = db.collection('shifts')
    .where('status', 'in', ['confirmed', 'expired'])
    .where('start', '>=', from)
    .where('start', '<=', to)

  if (isQuickShift) {
    query = query.where('boostedOptions.boostType', '==', 'quick')
    query = query.where('finalConfirmation.confirmed', '==', true)
  }

  if (assignedTo) {
    query = query.where('assignedTo', '==', assignedTo)
  }

  query.get()
    .then(snapshot => {
      const data = snapshot.docs.map(doc => {
        const item = doc.data()
        item.id = doc.id
        return item
      })

      let filteredData = []
      if (!isQuickShift && !getAll) {
        filteredData = data.filter(item => item.boostedOptions?.boostType !== 'quick')
      } else {
        filteredData = data
      }

      const finalData = filteredData.filter(item => (item.status === 'confirmed' || (item.status === 'expired' && item.workDayType === 'sick')))
      resolve(finalData)
    })
    .catch(() => {
      reject()
    })
})

const addBonus = async (payroll, user, period, activeTempContracts) => {
  const payrollCopy = { ...payroll }
  const activeTempContract = activeTempContracts[0]
  const firstDay = new Date(period.from.getFullYear(), period.from.getMonth(), 1)
  const lastDay = new Date(period.to.getFullYear(), period.to.getMonth() + 1, 0)
  if(!activeTempContract.bonus) {
    return payrollCopy
  }

  if(activeTempContract.bonus.recurring) {
    // Check when should the bonus be paid end of the month or middle of the month
    if(period.to.getDate() !== lastDay.getDate() && activeTempContract.bonus.paidEndOfMonth === true) {
      return payrollCopy
    }
    if(period.to.getDate() !== 15 && activeTempContract.bonus.paidEndOfMonth === false) {
      return payrollCopy
    }
  }
  let monthShifts = await fetchShifts({
    assignedTo: user.id,
    from: firstDay,
    to: lastDay,
    getAll: true,
  })

    monthShifts = monthShifts.filter(shift => shift.accountId === activeTempContract.accountId && shift.status === 'confirmed').map(shift => ({
      ...shift,
      start: shift.start.toDate(),
      end: shift.end.toDate(),
    }))
    // Check user has worked over 150 hours in the month
    const overHours = calculateOverHoursInPeriod(monthShifts, 149.8)
    if(overHours === 0) {
      return payrollCopy
    }
    
    // User has worked over 150 hours in the month add bonus
    const bonus = activeTempContract.bonus.amount || 0
    payrollCopy.bonus = bonus
    payrollCopy.items.push({
      title: `Bonus`,
      qty: 1,
      unit: 'kpl',
      code: 2130,
      cost: bonus,
      total: bonus,
    })
    payrollCopy.total += payrollCopy.bonus
    return payrollCopy
}

const getOverHoursInPeriod = async (payroll, jaksoPeriod, maxHours, user, activeTempContracts) =>{
  const payrollCopy = {...payroll}
  // Fetch shifts from jakso period
  try {
    let periodShifts = await fetchShifts({
      assignedTo: user.id,
      from: jaksoPeriod.start,
      to: jaksoPeriod.end,
      getAll: true,
    })
  // TODO: Iterate over tempContracts and filter shifts by areaId
  const overHoursByContract = activeTempContracts.map(activeTempContract => {
      periodShifts = periodShifts.filter(shift => shift.accountId === activeTempContract.accountId && shift.status === 'confirmed').map(shift => ({
        ...shift,
        start: shift.start.toDate(),
        end: shift.end.toDate(),
      }))
      const overHours = calculateOverHoursInPeriod(periodShifts, maxHours)
      // Get user
      const finalSalary = activeTempContract.contract.salary
      let specialSalary = false
      if (user.specials && user.specials.salary) {
        specialSalary = user.specials.salary ? user.specials.salary : null
      }
      const baseCost = getCost(user, specialSalary, finalSalary)
      return {
        overHours50: overHours > 18 ? 18 : overHours,
        overHours100: overHours > 18 ? overHours - 18 : 0,
        baseCost,
        userId: payrollCopy.userId,
      }
    })
    // Add overhours to payroll
  if(overHoursByContract) {
    overHoursByContract.forEach(overHours => {
      // Title in the form of: Jakso 5.6-25.6 ylityö 50%
      const title= `Jakso ${jaksoPeriod.start.toLocaleDateString('fi', {month: "numeric", day: "numeric"})} - ${jaksoPeriod.end.toLocaleDateString('fi', {month: "numeric", day: "numeric"})} ylityö`
      if(overHours.overHours50 > 0) {
        payrollCopy.overHoursTotal += overHours.baseCost * 0.5 * overHours.overHours50
        payrollCopy.hasJaksoOverHours = true
        payrollCopy.items.push({
          title: `${title} 50%` ,
          qty: overHours.overHours50,
          unit: 'tuntia',
          code: 1232,
          cost: overHours.baseCost * 0.5,
          total: overHours.baseCost * 0.5 * overHours.overHours50,
        })
      }
      if(overHours.overHours100 > 0) {
        payrollCopy.overHoursTotal += overHours.baseCost * overHours.overHours100
        payrollCopy.items.push({
          title: `${title} 100%`,
          qty: overHours.overHours100,
          unit: 'tuntia',
          code: 1233,
          cost: overHours.baseCost,
          total: overHours.baseCost * overHours.overHours100,
        })
      }
    })
  }
  return {...payrollCopy}
  } catch (error) {
    return {...payrollCopy}
  }
}

const createPayrolls = async (shifts, quickShift, period) => {
  await remoteConfig.fetchAndActivate()

  // Get holidays
  const holidaysConfig = JSON.parse(remoteConfig.getValue('holidays').asString())
  const holidayArrays = Object.entries(holidaysConfig).map(year => year[1])
  const holidays = [].concat(...holidayArrays)

  // Get eves
  const evesConfig = JSON.parse(remoteConfig.getValue('eves').asString())
  const evesArray = Object.entries(evesConfig).map(year => year[1])
  const eves = [].concat(...evesArray)

  // Create payrolls
  const payrollList = []
  const userIds = []
  const contractIds = []
  const contracts = {}
  const accountIds = []
  const accounts = {}
  const users = []
  // Calculate overhours in jakso period
  const baseThreeWeeksHours = period.maxHours
  const jaksoPeriod = getThreeWeekPeriod(period.from)

  // Get userIds
  shifts.forEach(shift => {
    if (!userIds.includes(shift.assignedTo)) {
      userIds.push(shift.assignedTo)
      users.push({ ...shift.user, id: shift.assignedTo })
    }
    if (!contractIds.includes(shift.contractId)) {
      contractIds.push(shift.contractId)
    }
    if (!accountIds.includes(shift.accountId)) {
      accountIds.push(shift.accountId)
    }
  })

  // Get unique contracts
  await Promise.all(contractIds.map(async contractId => {
    const doc = await db.collection('contracts').doc(contractId).get()
    if(doc.exists) {
      const contract = doc.data()
      contract.id = doc.id
      contracts[contractId] = contract
    }
  }))

  // Get unique accounts
  await Promise.all(accountIds.map(async accountId => {
    const doc = await db.collection('accounts').doc(accountId).get()
    if(doc.exists) {
      const account = doc.data()
      account.id = doc.id
      accounts[accountId] = account
    }
  }))

  await Promise.all(users.map(async user => {
    const userShifts = shifts.filter(shift => shift.assignedTo === user.id)
    let payroll = {
      userId: user.id,
      date: new Date(),
      total: 0,
      items: [],
      nonTaxable: [],
      nonTaxableTotal: 0,
      from: period && period.from ? period.from : userShifts.map(i => i.start).sort((a, b) => b - a).pop(),
      to: period && period.to ? period.to : userShifts.map(i => i.end).sort((a, b) => a - b).pop(),
      status: 'pending',
      type: quickShift ? 'quick' : 'normal',
      shifts: userShifts.map(i => i.id),
      overHoursTotal: 0,
    }
    const activeTempContracts = []
    await Promise.all(userShifts.map(async shift => {
      const {id} = shift
      shift.start = shift.start instanceof Date ? shift.start : shift.start.toDate()
      shift.end = shift.end instanceof Date ? shift.end : shift.end.toDate()
      let specialSalary = false
      if (user.specials && user.specials.salary) {
        specialSalary = user.specials.salary ? user.specials.salary : null
      }

      let contract = contracts[shift.contractId]
      let finalSalary = contract.salary
      const matchedTempContract = checkTempContractShift({ ...shift, user })
      if (matchedTempContract && matchedTempContract.contractId) {
        const contractData = await fetchContract(matchedTempContract.contractId)
        if(activeTempContracts.filter(i => i.contractId === matchedTempContract.contractId).length === 0) {
          activeTempContracts.push({
            ...matchedTempContract,
            contract: contractData,
          })
        }
        if (contractData && contractData.salary) {
          finalSalary = contractData.salary
          contract = contractData
        }
      } else if (shift.campaignId && shift.campaignDetails) {
        finalSalary = shift.campaignDetails.salary
      }
      const baseCost = getCost(user, specialSalary, finalSalary)
      const unpaidTime = contract.unpaidTime || 0
      const isHoliday = await getHoliday(shift.startStr, shift.endStr, holidays)
      const isEveningHoliday = await getEve(shift.end, shift.endStr, eves)
      const shiftAccount = accounts[shift.accountId]
      const isPublic = shiftAccount && shiftAccount.accountType === 'public'
      const report = calculateShift(shift.start, shift.end, baseCost, isHoliday, isEveningHoliday, unpaidTime, isPublic)
      const title = shift.start.toLocaleString('fi', { month: 'numeric', day: 'numeric' })
      if (shift.workDayType === 'sick') {
        const qty = report.perus.hours
        const cost = baseCost
        const total = cost * qty
        payroll.total += total
        payroll.items.push({
          title: `${title} sairaslomakorvaus`,
          qty,
          unit: 'tuntia',
          code: 2410,
          cost,
          total,
          shiftId: id,
        })
      }
      if (shift.workDayType !== 'sick') {
        if (shift.kilometrikorvaus && shift.kilometrikorvaus > 0) {
          const qty = shift.kilometrikorvaus
          const cost = 0.53 // In EUR, km allowance 2023, see more https://www.veronmaksajat.fi/Palkka-ja-elake/Kilometrikorvaukset/kilometrikorvaukset-2023/
          const total = cost * qty
          payroll.nonTaxableTotal += total
          const item = {
            title: `${title} kilometrikorvaus`,
            qty,
            code: 4050,
            unit: 'km',
            cost,
            total,
            shiftId: id,
          }
          payroll.nonTaxable.push(item)
        }
        if (shift.boosted && shift.boostedOptions.boostType === 'bonus') {
          const qty = 1
          const cost = shift.boostedOptions.value
          const total = cost * qty
          payroll.total += total
          payroll.items.push({
            title: `${title} bonus`,
            qty,
            code: 1495,
            unit: 'kpl',
            cost,
            total,
            shiftId: id,
          })
        }
        if (report.perus) {
          const qty = report.perus.hours
          const cost = baseCost
          const total = cost * qty
          payroll.total += total
          payroll.items.push({
            title: quickShift ? `${title} pikapalkka` : title,
            qty,
            unit: 'tuntia',
            code: 1004,
            cost,
            total,
            shiftId: id,
          })
        }
        if (report.iltaLisa) {
          const qty = report.iltaLisa.hours
          const cost = baseCost * 0.15
          const total = cost * qty
          payroll.total += total
          payroll.items.push({
            title: `${title} iltalisä`,
            qty,
            code: 1447,
            unit: 'tuntia',
            cost,
            total,
            shiftId: id,
          })
        }
        if (report.yoLisa) {
          const percentage = matchedTempContract && matchedTempContract.contractId ? 0.40 : 0.30
          const qty = report.yoLisa.hours
          const cost = baseCost * percentage
          const total = cost * qty
          payroll.total += total
          payroll.items.push({
            title: `${title} yölisä`,
            qty: report.yoLisa.hours,
            unit: 'tuntia',
            code: 1448,
            cost,
            total,
            shiftId: id,
          })
        }
        if (report.lauantaiLisa) {
          const qty = report.lauantaiLisa.hours
          const cost = baseCost * 0.25
          const total = cost * qty
          payroll.total += total
          payroll.items.push({
            title: `${title} lauantailisä`,
            qty,
            code: 1454,
            unit: 'tuntia',
            cost,
            total,
            shiftId: id,
          })
        }
        if (report.sunnuntaiLisa) {
          const lisaTitle = report.isHoliday ? `${title} pyhälisä` : `${title} sunnuntailisä`
          const qty = report.sunnuntaiLisa.hours
          const cost = baseCost
          const total = cost * qty
          payroll.total += total
          payroll.items.push({
            title: lisaTitle,
            qty,
            code: report.isHoliday ? 2340 : 1410, // arkipyhäkorvaus 2340
            unit: 'tuntia',
            cost,
            total,
            shiftId: id,
          })
        }
        if (report.ylityo && !contract.noOverHours && !matchedTempContract) {
          if (report.ylityo.first) {
            const qty = report.ylityo.first.hours
            const cost = baseCost * 0.5
            const total = cost * qty
            payroll.overHoursTotal += total
            payroll.items.push({
              title: `${title} ylityö 50%`,
              qty,
              code: 1230,
              unit: 'tuntia',
              cost,
              total,
              shiftId: id,
            })
          }
          if (report.ylityo.second) {
            const qty = report.ylityo.second.hours
            const cost = baseCost
            const total = cost * qty
            payroll.overHoursTotal += total
            payroll.items.push({
              title: `${title} ylityö 100%`,
              qty,
              code: 1231,
              unit: 'tuntia',
              cost,
              total,
              shiftId: id,
            })
          }
        }
      }
    }))
    // Check if jakso period end is between period from and to
    if (period.addPeriodOvertime && jaksoPeriod.end >= period.from && jaksoPeriod.end <= period.to) {
      payroll = await getOverHoursInPeriod(payroll, jaksoPeriod, baseThreeWeeksHours, user, activeTempContracts)
    }
    if(activeTempContracts && activeTempContracts.length > 0) {
      payroll = await addBonus(payroll, user, period, activeTempContracts)
    }
    payroll.subTotal = payroll.total
    // Lomakorvaus
    if (!user.isJoiku || !user.noHolidayPay) {
      payroll.holidayPay = 0.09
      payroll.total += payroll.total * 0.09
    }

    // Add the non taxable to total
    const total = payroll.total + payroll.nonTaxableTotal + payroll.overHoursTotal
    payroll.total = Math.round(total * 100) / 100
    payrollList.push(payroll)
  }))
  
  return payrollList
}

const createElementPdf = invoiceData => `
    <div class="col-12 card invoice-preview-card">
      <div class="card-body invoice-padding pb-0">
        <div class="d-flex justify-content-between flex-md-row flex-column invoice-spacing mt-0">
          <div>
            <div class="logo-wrapper">
              <span class="brand-logo">
                <img src="require(${`@hw-ui/assets/images/logo/${store.state.app.appConfig.logoName}`})" alt="logo" class="" style="width: 50px;">
              </span>
              <h3 class="invoice-logo"> ${store.state.app.appConfig.name} </h3>
            </div>
          </div>
          <div class="mt-md-2 mt-2">
            <h4 class="invoice-title"> Palkkalaskelma </h4>
            <div class="invoice-date-wrapper">
              <p class="invoice-date-title" style="width: 12rem;"> Ajalta: </p>
              <p class="invoice-date"> ${invoiceData.from.toDate().toLocaleDateString('fi')} </p>
            </div>
            <div class="invoice-date-wrapper">
              <p class="invoice-date-title" style="width: 12rem;"></p>
              <p class="invoice-date">  ${invoiceData.to.toDate().toLocaleDateString('fi')} </p>
            </div>
          </div>
        </div>
      </div>
      <hr class="invoice-spacing">
      ${invoiceData.user ? `<div class="card-body invoice-padding pt-0">
          <div class="row invoice-spacing">
            <div class="p-0 col-xl-6 col-12">
              <h6 class="mb-25" style="text-transform: uppercase;"> ${invoiceData.user.fullName} </h6>
              <p class="card-text mb-25"> ${invoiceData.user.email} </p>
            </div>
          </div>
        </div>` : `
        `}
      <table role="table" class="table b-table b-table-fixed">
        <thead>
          <tr>
            <th role="columnheader" scope="col" aria-colindex="1" aria-label="Title" class="">
              <div></div>
            </th>
            <th role="columnheader" scope="col" aria-colindex="2" class="">
              <div>Määrä</div>
            </th>
            <th role="columnheader" scope="col" aria-colindex="3" class="">
              <div>Yksikkö</div>
            </th>
            <th role="columnheader" scope="col" aria-colindex="4" class="">
              <div>Hinta</div>
            </th>
            <th role="columnheader" scope="col" aria-colindex="5" class="">
              <div>Yhteensä</div>
            </th>
          </tr>
        </thead>
        <tbody role="rowgroup">
          ${invoiceData.items.map(item => `
            <tr role="row" class="">
              <td aria-colindex="1" role="cell" class=""> ${item.title}</td>
              <td aria-colindex="2" role="cell" class="">
                <p class="card-text mb-25"> ${parseFloat(item.qty).toFixed(2)}</p>
              </td>
              <td aria-colindex="3" role="cell" class=""> ${item.unit}</td>
              <td aria-colindex="4" role="cell" class="">
                <p class="card-text mb-25"> ${parseFloat(item.cost).toFixed(2)} €</p>
              </td>
              <td aria-colindex="5" role="cell" class="">
                <p class="card-text mb-25"> ${item.total.toFixed(2)} €</p>
              </td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      ${invoiceData.nonTaxable && invoiceData.nonTaxable.length > 0 ? `
      <table role="table" aria-busy="false" aria-colcount="5" class="table b-table b-table-fixed" id="__BVID__171">
        <thead role="rowgroup" class="">
          <tr role="row" class="">
            <th role="columnheader" scope="col" aria-colindex="1" aria-label="Title" class="">
              <div>Korvaus</div>
            </th>
            <th role="columnheader" scope="col" aria-colindex="2" class="">
              <div>Määrä</div>
            </th>
            <th role="columnheader" scope="col" aria-colindex="3" class="">
              <div>Yksikkö</div>
            </th>
            <th role="columnheader" scope="col" aria-colindex="4" class="">
              <div>Hinta</div>
            </th>
            <th role="columnheader" scope="col" aria-colindex="5" class="">
              <div>Yhteensä</div>
            </th>
          </tr>
        </thead>
        <tbody role="rowgroup">
          ${invoiceData.nonTaxable.map(item => `
            <tr role="row" class="">
              <td aria-colindex="1" role="cell" class="">${item.title}</td>
              <td aria-colindex="2" role="cell" class="">
                <p class="card-text mb-25"> ${parseFloat(item.qty).toFixed(2)} </p>
              </td>
              <td aria-colindex="3" role="cell" class="">${item.unit}</td>
              <td aria-colindex="4" role="cell" class="">
                <p class="card-text mb-25"> ${parseFloat(item.cost).toFixed(2)} €</p>
              </td>
              <td aria-colindex="5" role="cell" class="">
                <p class="card-text mb-25"> ${item.total.toFixed(2)} €</p>
              </td>
            </tr>
          `).join('')}
        </tbody>
      </table>` : `
      `}
      ${invoiceData.holidayPay ? `
      <div>
        <hr>
        <table role="table" aria-busy="false" aria-colcount="5" class="table b-table" id="__BVID__181">
          <thead role="rowgroup" class="">
            <tr role="row" class="">
              <th role="columnheader" scope="col" aria-colindex="1" class="">
                <div>Lisä</div>
              </th>
              <th role="columnheader" scope="col" aria-colindex="2" class="">
                <div>Määrä</div>
              </th>
              <th role="columnheader" scope="col" aria-colindex="3" class="">
                <div>Yksikkö</div>
              </th>
              <th role="columnheader" scope="col" aria-colindex="4" class="">
                <div>Summasta</div>
              </th>
              <th role="columnheader" scope="col" aria-colindex="5" class="">
                <div>Yhteensä</div>
              </th>
            </tr>
          </thead>
          <tbody role="rowgroup">
              <tr role="row" class="">
                <td aria-colindex="1" role="cell" class="">Lomakorvaus</td>
                <td aria-colindex="2" role="cell" class="">9%</td>
                <td aria-colindex="3" role="cell" class="">%</td>
                <td aria-colindex="4" role="cell" class="">${invoiceData.subTotal.toFixed(2)}€</td>
                <td aria-colindex="5" role="cell" class="">${(invoiceData.subTotal * invoiceData.holidayPay).toFixed(2)}€</td>
              </tr>
          </tbody>
        </table>
      </div>` : `
      `}
      <hr class="invoice-spacing">
      <div class="card-body invoice-padding pb-0">
        <div class="row">
          <div class="mt-md-0 mt-3 col-md-6 order-md-1 col-12 order-2"></div>
          <div class="mt-md-6 d-flex justify-content-end col-md-6 order-md-2 col-12 order-1">
            <div class="invoice-total-wrapper">
              <div class="invoice-total-item">
                <p class="invoice-total-title"> Yhteensä veroton: </p>
                <p class="invoice-total-amount"> ${invoiceData.total}€ </p>
              </div>
              <hr class="my-50">
            </div>
          </div>
        </div>
      </div>
      <hr class="invoice-spacing">
    </div>
  `

const markAsPaid = ({ id, status }) => new Promise((resolve, reject) => {
  db.collection('payrolls').doc(id).update({ status })
    .then(() => {
      resolve()
    })
    .catch(() => {
      reject()
    })
})

const markAsSent = ({ id }) => new Promise((resolve, reject) => {
  db.collection('payrolls').doc(id).update({ sent: true })
    .then(() => resolve())
    .catch(() => reject())
})

const fetchPayroll = ({ id }) => new Promise((resolve, reject) => {
  db.collection('payrolls')
    .doc(id)
    .get()
    .then(async doc => {
      const data = doc.data()
      data.id = id
      const userDoc = await db.collection('users').doc(data.userId).get()
      data.user = userDoc.data()
      data.user.id = userDoc.id
      resolve({ ...data, client: data })
    })
    .catch(error => {
      reject(error)
    })
})

const fetchPayrolls = ({ status, isQuickShift }) => new Promise((resolve, reject) => {
  db.collection('payrolls')
    .where('status', '==', status.value)
    .where('type', '==', isQuickShift ? 'quick' : 'normal')
    .orderBy('date', 'desc')
    .get()
    .then(async snapshot => {
      const data = await Promise.all(snapshot.docs.map(async doc => {
        const item = doc.data()
        item.id = doc.id
        item.status = (item.status === 'pending' && item.sent) ? 'sent' : item.status
        try {
          const userDoc = await db.collection('users').doc(item.userId).get()
          item.user = userDoc.data()
          item.user.isUserNew = await db.collection('payrolls')
            .where('userId', '==', item.userId)
            .where('status', '==', 'paid')
            .get()
            .then(res => (res.docs.length === 0))
            .catch(() => false)
        } catch {
          item.user = {
            fullName: 'Not Found',
          }
        }
        return item
      }))
      resolve({ data })
    })
    .catch(() => {
      reject()
    })
})

const deletePayroll = ({ id }) => new Promise((resolve, reject) => {
  db.collection('payrolls').doc(id).update({
    status: 'deleted',
  })
    .then(() => {
      resolve()
    })
    .catch(error => {
      reject(error)
    })
})

const fetchUser = ({ id }) => new Promise((resolve, reject) => {
  db.collection('users')
    .doc(id)
    .get()
    .then(document => {
      if (!document.exists) {
        reject()
      }
      const data = document.data()
      const role = store.getters.userRole(data.role) // TODO do this should be in vuex mutation not here!
      resolve({ ...data, id: document.id, role })
    })
    .catch(() => {
      reject()
    })
})

const isUserNewToPayroll = async ({ id, start }) => {
  try {
    const { docs } = await db.collection('payrolls')
      .where('userId', '==', id)
      .where('status', '==', 'paid')
      .get()
      
    if (docs.length) {
      return false
    }
  
    // Get latest confirmed shift
    const snapshot = await db.collection('shifts')
      .where('assignedTo', '==', id)
      .where('status', '==', 'confirmed')
      .where('start', '<', start)
      .orderBy('start', 'desc')
      .limit(1)
      .get()
  
    return snapshot.docs.length === 0 // user is new to payroll (no shift confimed && no payroll before)
  } catch (error) {
    return Promise.reject(error)
  }
}

const exportCSVList = payrolls => {
  let csv = ''
  payrolls.forEach(payroll => {
    payroll.items.forEach(item => {
      const row = []
      row.push('E') // Row type
      row.push(payroll.userId) // User ID
      row.push(item.code ? item.code : 1004) // Payment code 1004 is hourpay
      row.push(item.title) // Payment title
      row.push(parseFloat(item.qty).toFixed(2)) // Qty
      row.push(parseFloat(item.cost).toFixed(2)) // Amount
      csv += row.join(';')
      csv += '\n'
    })
    if (payroll.nonTaxable.length > 0) {
      payroll.nonTaxable.forEach(item => {
        const row = []
        row.push('E') // Row type
        row.push(payroll.userId) // User ID
        row.push(item.code ? item.code : 4050) // Payment code 1004 is hourpay
        row.push(item.title) // Payment title
        row.push(parseFloat(item.qty).toFixed(2)) // Qty
        row.push(parseFloat(item.cost).toFixed(2)) // Amount
        csv += row.join(';')
        csv += '\n'
      })
    }
    if (payroll.holidayPay) {
      const holidayRow = []
      holidayRow.push('E') // holidayRow type
      holidayRow.push(payroll.userId) // User ID
      holidayRow.push(2206) // Payment code 2206 is hourpay
      holidayRow.push('Lomakorvaus') // Payment title
      holidayRow.push(1) // Qty
      holidayRow.push(parseFloat(payroll.subTotal * payroll.holidayPay).toFixed(2)) // Amount
      csv += holidayRow.join(';')
      csv += '\n'
    }
  })
  const hiddenElement = document.createElement('a')
  hiddenElement.href = `data:text/csv;base64,${btoa(csv)}`
  hiddenElement.target = '_blank'
  // provide the name for the CSV file to be downloaded
  hiddenElement.download = 'Palkkalista.csv'
  hiddenElement.click()
}

const updatePayroll = ({
  id, total, nonTaxable, subTotal, items, edited, editedOn, editedBy,
}) => new Promise((resolve, reject) => {
  db.collection('payrolls').doc(id).update({
    total,
    subTotal,
    nonTaxable,
    items,
    edited,
    editedOn,
    editedBy,
  })
    .then(() => {
      resolve()
    })
    .catch(error => {
      reject(error)
    })
})

const sendPayrolls = async payrollList => new Promise((resolve, reject) => {
  const messages = payrollList.map(payroll => {
    const from = payroll.from.toDate().toLocaleString('fi', {
      month: 'numeric',
      day: 'numeric',
    })
    const to = payroll.to.toDate().toLocaleString('fi', {
      month: 'numeric',
      day: 'numeric',
    })
    const {
      firstName,
      email,
    } = payroll.user
    const sender = 'No Reply <noreply@hoiwa.fi>'
    const subject = `Tarkista vuorosi ajalta ${from} - ${to}`
    const message = `
    <p style="text-transform: capitalize">Hei ${firstName}!</p>
                  <p>Vuorosi ajalta <b>${from} - ${to}</b>
                  on nytten näkyvissä Hoiwa sovelluksesta.
                  <br> 
                  <b>Tämä lähetetään jotta voit tarkastaa 
                  tekemäsi työpäivät ja tunnit.</b>
                  <p>Kyselyt ja muu palkkaan liittyvät kysymykset 
                  voi laittaa palkkalaskijoilemme osoitteeseen
                  <mailto:b>palkka@hoiwa.fi</b><p>
                  <p>Ystävällisin terveisin,</p>
                  <p>Hoiwa ‑tiimi</p>
              `
    return {
      from: sender, subject, html: message, to: email,
    }
  })

  auth.currentUser.getIdToken(true).then(idToken => {
    axios.post(`${store.state.app.appConfig.type === 'hw-admin' ? functions.url : functions.oiwaUrl}/sendEmails`, { messages, token: idToken })
      .then(async () => {
        await Promise.all(payrollList.map(async payroll => {
          await markAsSent({ id: payroll.id })
        }))
        resolve()
      })
      .catch(error => reject(error))
  })
    .catch(error => {
      reject(error)
    })
})

const downloadPayroll = async payroll => {
  const element = createElementPdf(payroll)
  const from = payroll.from.toDate().toLocaleDateString('fi')
  const to = payroll.to.toDate().toLocaleDateString('fi')
  await html2pdf().set({ margin: 10 }).from(element).save(`palkka_${payroll.user.fullName}_ ${from}_${to}.pdf`)
}

export {
  getSalary,
  savePayrolls,
  createPayrolls,
  createElementPdf,
  markAsPaid,
  markAsSent,
  fetchPayroll,
  fetchPayrolls,
  deletePayroll,
  fetchShifts,
  fetchUser,
  isUserNewToPayroll,
  exportCSVList,
  updatePayroll,
  downloadPayroll,
  sendPayrolls,
}
